import { formatDateString, formatTimeString, formatWeekday, getRepeatEndDate } from 'tsx/libs/dayjs';
import { ExportProps } from 'tsx/types/exportProps';

import { selectAppointmentRepeatFrequencies } from '~appointments/selectors/repeatFrequencies';
import { selectLookupAppointmentServiceTypes } from '~appointments/selectors/serviceTypes';
import { selectAppointmentStatuses } from '~appointments/selectors/statuses';
import { selectClientPackages } from '~main/selectors/clients/clientPackages';

const fields: ExportProps = {
  client_name: {
    type: 'text-like',
    caption: 'Client',
    checked: true,
    field: 'client.full_name',
    placeholder: 'Filter..',
    columnDef: {
      id: 'client.full_name',
      header: 'Client',
      type: 'text',
      deriveValue: (row) => row.client?.full_name ?? '',
    },
  },
  care_worker: {
    type: 'text-like',
    caption: 'Care Worker',
    checked: true,
    field: 'user.full_name',
    placeholder: 'Filter..',
    columnDef: {
      id: 'user.full_name',
      header: 'Care Worker',
      type: 'text',
      deriveValue: (row) => row.user?.full_name ?? '',
    },
  },
  referrer: {
    type: 'text-like',
    field: 'client.referrer_name',
    caption: 'Referrer',
    placeholder: 'Filter..',
    columnDef: {
      id: 'client.referrer_name',
      header: 'Referrer',
      type: 'text',
      deriveValue: (row) => row.client?.referrer_name ?? '',
    },
  },
  region_name: {
    type: 'text-like',
    caption: 'Client Region',
    field: 'client.region.name',
    placeholder: 'Filter..',
    columnDef: {
      id: 'client.region.name',
      header: 'Client Region',
      type: 'text',
      deriveValue: (row) => row.client?.region?.name ?? '',
    },
  },
  repeat_start: {
    type: 'date-range',
    caption: 'Repeat Start',
    field: 'repeat_start',
    columnDef: {
      id: 'repeat_start',
      header: 'Repeat Start',
      type: 'text',
      deriveValue: (row) => formatDateString(row.repeat_start),
    },
  },
  repeat_end: {
    type: 'date-range',
    caption: 'Repeat End',
    field: 'repeat_end',
    columnDef: {
      id: 'repeat_end',
      header: 'Repeat End',
      type: 'text',
      deriveValue: (row) => getRepeatEndDate(row.repeat_end),
    },
  },
  start_time: {
    type: 'time-range',
    field: 'start_time',
    caption: 'Start Time',
    columnDef: {
      id: 'start_time',
      header: 'Start Time',
      type: 'text',
      deriveValue: (row) => formatTimeString(row?.start_time ?? ''),
    },
  },
  end_time: {
    type: 'time-range',
    field: 'end_time',
    caption: 'End Time',
    columnDef: {
      id: 'end_time',
      header: 'End Time',
      type: 'text',
      deriveValue: (row) => formatTimeString(row?.end_time ?? ''),
    },
  },
  duration: {
    type: 'text',
    caption: 'Duration (mins)',
    field: 'duration',
    columnDef: {
      id: 'duration',
      header: 'Duration (mins)',
      type: 'text',
    },
  },
  repeat_dates: {
    type: 'multi-lookup',
    caption: 'Week Repeat Day',
    field: 'repeat_weekday',
    options: Array.from({ length: 7 }, (_, index) => {
      const day = formatWeekday(index + 1, true);
      return { id: day, name: day };
    }),
    placeholder: 'All',
    columnDef: {
      id: 'repeat_weekday',
      header: 'Week Day',
      type: 'text',
      deriveValue: (row) => row.repeat_frequency?.day ?? '',
    },
  },
  week_count: {
    type: 'multi-lookup',
    caption: 'Week Repeat Count',
    field: 'repeat_week_count',
    selectorOptions: selectAppointmentRepeatFrequencies,
    placeholder: 'All',
    columnDef: {
      id: 'repeat_week_count',
      header: 'Week Repeat Count',
      type: 'text',
      deriveValue: (row) => row.repeat_frequency?.frequency ?? '',
    },
  },
  status: {
    type: 'multi-lookup',
    field: 'status_id',
    columnField: 'status.name',
    caption: 'Status',
    placeholder: 'All',
    selectorOptions: selectAppointmentStatuses,
    columnDef: {
      id: 'status_id',
      header: 'Status',
      type: 'text',
      deriveValue: (row) => row.status?.name ?? '',
    },
  },
  service_type: {
    type: 'multi-lookup',
    field: 'service_type_id',
    columnField: 'service_type.name',
    caption: 'Service Type',
    placeholder: 'All',
    selectorOptions: selectLookupAppointmentServiceTypes,
    columnDef: {
      id: 'service_type.name',
      header: 'Service Type',
      type: 'text',
      deriveValue: ({ service_type }) =>
        service_type ? `${service_type.name}${service_type.deleted ? ' - DELETED' : ''}` : '',
    },
  },
  client_package: {
    type: 'multi-lookup',
    field: 'package_fixed_id',
    columnField: 'package_fixed.name',
    caption: 'Package',
    placeholder: 'All',
    selectorOptions: selectClientPackages,
    columnDef: {
      id: 'package_fixed.name',
      header: 'Package',
      type: 'text',
      deriveValue: (row) => row.package_fixed?.name ?? '',
    },
  },
};

export default fields;
